import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ENROL_URL, LOGO_URL, NAV_LINKS } from "../lib/site";
import LanguageSwitcher from "./LanguageSwitcher";

const Navbar = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `text-base font-medium transition-colors duration-200 pb-1 border-b-2 ${
      isActive ? "text-navy border-waratah" : "text-slate-600 border-transparent hover:text-navy"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-navy-100" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link" aria-label="Telopea Park OSHC home">
            <img src={LOGO_URL} alt="Telopea Park OSHC Waratah logo" className="h-12 w-12 rounded-full object-cover ring-1 ring-navy-100 mix-blend-multiply" />
            <div className="leading-tight">
              <span className="block font-display text-lg sm:text-xl font-bold text-navy">Telopea Park OSHC</span>
              <span className="block text-xs uppercase tracking-[0.18em] text-slate-500">A Home Away From Home</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.to === "/"} className={linkClass} data-testid={link.testId}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <LanguageSwitcher />
            </div>
            <a
              href={ENROL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-enrol-button"
              className="hidden sm:inline-flex items-center rounded-full bg-waratah px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-waratah-600 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
            >
              Enrol Now
            </a>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex h-12 w-12 items-center justify-center rounded-full border border-navy-100 text-navy transition-colors duration-200 hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-button"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-x-0 top-20 bottom-0 z-40 bg-white overflow-y-auto" data-testid="mobile-nav">
          <nav className="flex flex-col px-6 py-8 gap-1" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${link.testId}`}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-4 text-lg font-medium transition-colors duration-200 ${
                    isActive ? "bg-surface text-navy" : "text-slate-600 hover:bg-surface hover:text-navy"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={ENROL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-enrol-button"
              className="mt-4 inline-flex items-center justify-center rounded-full bg-waratah px-6 py-4 text-lg font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-waratah-600"
            >
              Enrol Now
            </a>
            <div className="mt-3">
              <LanguageSwitcher mobile onSelect={() => setOpen(false)} />
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;
